'use client';

import { EspeceSelect, especesToSelectOptions } from '@repo/ui/features/especes';
import { Alert, AlertDescription } from '@repo/ui/components/ui/alert';
import { Badge } from '@repo/ui/components/ui/badge';
import { Card, CardContent, CardHeader } from '@repo/ui/components/ui/card';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { Ruler, TreePine, Hash } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import { FormData } from '../../../shared/types';
import { AlertCircle } from 'lucide-react';

interface IdentificationStepProps {
  formData: FormData;
  errors: { [key: string]: string };
  champignons?: any[];
  especes?: any[];
  nuisibles?: any[];
  onChange: (fieldName: string, value: any) => void;
  renderFreeforms: (fieldName: string) => React.ReactNode;
}

export function IdentificationStep({
  formData,
  errors,
  champignons,
  especes,
  nuisibles,
  onChange,
  renderFreeforms,
}: IdentificationStepProps) {
  const especeOptions = useMemo(
    () => especesToSelectOptions(especes || [], { includeData: true }),
    [especes]
  );

  const especeValue = formData._espece?.id?.toString() ?? formData.espece?.toString() ?? undefined;

  const handleEspeceChange = useCallback(
    (value: string | null) => {
      if (!value) {
        onChange('_espece', null);
        onChange('espece', null);
        onChange('valeur-intrinseque-indice-espece', null);
        return;
      }
      const selectedEspece = especes?.find((e) => e.id === Number(value));
      if (selectedEspece) {
        onChange('_espece', selectedEspece);
        onChange('espece', selectedEspece.id);
        onChange('valeur-intrinseque-indice-espece', selectedEspece.indice_espece);
      }
    },
    [especes, onChange]
  );

  const renderFieldError = (fieldName: string) => {
    if (!errors[fieldName]) return null;
    return (
      <Alert
        variant="destructive"
        className="mt-2 border-destructive/50 bg-destructive/10 text-destructive ring-1 ring-destructive/20 dark:border-destructive/50 dark:bg-destructive/10 dark:ring-destructive/30"
        role="alert"
      >
        <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
        <AlertDescription className="text-xs font-medium">{errors[fieldName]}</AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Section Header */}
      <div className="space-y-2">
        <h2 className="text-lg font-semibold leading-tight text-foreground sm:text-xl">
          Identification de l'arbre
        </h2>
        <p className="text-xs font-medium leading-relaxed text-muted-foreground/90 dark:text-muted-foreground/80 sm:text-sm">
          Remplissez les informations de base pour identifier l'arbre
        </p>
      </div>

      <div className="space-y-6">
        {/* Mesures Section */}
        <Card className="border-border/60 bg-card shadow-sm ring-1 ring-border/20 dark:border-border/80 dark:ring-border/30">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                <Ruler className="h-4.5 w-4.5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                Mesures physiques
              </h3>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Hauteur */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="hauteur" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                    Hauteur
                    <span className="ml-1 text-destructive">*</span>
                  </Label>
                  <Badge
                    variant="secondary"
                    className="border-border/40 bg-muted/50 px-2 py-0.5 text-xs font-medium shadow-sm dark:border-border/30"
                  >
                    mètres
                  </Badge>
                </div>
                <Input
                  id="hauteur"
                  type="number"
                  value={formData.hauteur || ''}
                  onChange={(e) => onChange('hauteur', Number(e.target.value))}
                  placeholder="Ex: 15.5"
                  step="0.1"
                  min="0"
                  className={`h-10 border-border/60 bg-background text-sm transition-all duration-200 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/20 ${errors.hauteur
                    ? 'border-destructive/60 focus-visible:ring-destructive/20'
                    : 'focus-visible:ring-primary/20'
                    }`}
                />
                {renderFieldError('hauteur')}
              </div>

              {/* Diamètre */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="diametre" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                    Diamètre à 1,3 m
                    <span className="ml-1 text-destructive">*</span>
                  </Label>
                  <Badge
                    variant="secondary"
                    className="border-border/40 bg-muted/50 px-2 py-0.5 text-xs font-medium shadow-sm dark:border-border/30"
                  >
                    centimètres
                  </Badge>
                </div>
                <Input
                  id="diametre"
                  type="number"
                  value={formData.diametre || ''}
                  onChange={(e) => onChange('diametre', Number(e.target.value))}
                  placeholder="Ex: 45"
                  step="0.1"
                  min="0"
                  className={`h-10 border-border/60 bg-background text-sm transition-all duration-200 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/20 ${errors.diametre
                    ? 'border-destructive/60 focus-visible:ring-destructive/20'
                    : 'focus-visible:ring-primary/20'
                    }`}
                />
                {renderFieldError('diametre')}
              </div>

              {/* Âge estimé */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="age-estime" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                    Âge estimé
                  </Label>
                  <Badge
                    variant="secondary"
                    className="border-border/40 bg-muted/50 px-2 py-0.5 text-xs font-medium shadow-sm dark:border-border/30"
                  >
                    années
                  </Badge>
                </div>
                <Input
                  id="age-estime"
                  type="number"
                  value={formData.age_estime || ''}
                  onChange={(e) => onChange('age_estime', e.target.value)}
                  placeholder="Ex: 25"

                  className={`h-10 border-border/60 bg-background text-sm transition-all duration-200 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/20 ${errors.age_estime
                    ? 'border-destructive/60 focus-visible:ring-destructive/20'
                    : 'focus-visible:ring-primary/20'
                    }`}
                />
                {renderFieldError('age_estime')}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Identification Section */}
        <Card className="border-border/60 bg-card shadow-sm ring-1 ring-border/20 dark:border-border/80 dark:ring-border/30">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                <TreePine className="h-4.5 w-4.5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                Identification
              </h3>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Espèce */}
            <div className="space-y-2.5">
              <Label htmlFor="espece" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                Espèce
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <EspeceSelect
                key={`espece-${formData._espece?.id ?? formData.espece ?? 'empty'}`}
                options={especeOptions as any}
                value={especeValue}
                onChange={handleEspeceChange}
                placeholder="Sélectionnez une espèce..."
                variant="form"
                error={!!errors.espece}
                searchMode="server"
                className="react-select-container"
              />
              {renderFieldError('espece')}
            </div>

            <div className="border-t border-border/60 pt-6 dark:border-border/80">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                  <Hash className="h-4 w-4 text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                  Informations supplémentaires
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Code site */}
                <div className="space-y-2.5">
                  <Label htmlFor="code_site" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                    Code site
                  </Label>
                  <Input
                    id="code_site"
                    type="text"
                    value={formData.code_site || ''}
                    onChange={(e) => onChange('code_site', e.target.value)}
                    placeholder="Ex: SITE-001"
                    className={`h-10 border-border/60 bg-background text-sm transition-all duration-200 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/20 ${errors.code_site
                      ? 'border-destructive/60 focus-visible:ring-destructive/20'
                      : 'focus-visible:ring-primary/20'
                      }`}
                  />
                  {renderFieldError('code_site')}
                </div>

                {/* Numéro sujet */}
                <div className="space-y-2.5">
                  <Label htmlFor="num_sujet" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                    Numéro sujet
                  </Label>
                  <Input
                    id="num_sujet"
                    type="text"
                    value={formData.num_sujet || ''}
                    onChange={(e) => onChange('num_sujet', e.target.value)}
                    placeholder="Ex: SUJ-0001"
                    className={`h-10 border-border/60 bg-background text-sm transition-all duration-200 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/20 ${errors.num_sujet
                      ? 'border-destructive/60 focus-visible:ring-destructive/20'
                      : 'focus-visible:ring-primary/20'
                      }`}
                  />
                  {renderFieldError('num_sujet')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
